import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import FillPlaceholder from '@/assets/images/icon-imagefill-placeholder.svg';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import ColorHelper from 'color';
import { getRgbaFromHex, getGradientString, isValidHex } from '@builder4/shared/utils/styleHelpers';
import type { Color, Gradient, RGBA } from '@builder4/shared/types/layout';
import { ColorPicker, type Palette } from '@/components/inspector-fields/ColorPicker/ColorPicker';

type PickerChangePayload = {
  mode: 'solid' | 'gradient';
  hex: string; // no '#'
  opacity: number; // 0..100
  gradient?: Gradient;
  reference?: string;
};

interface ColorFieldProps {
  className: string;
  text?: string;
  inputValue?: string;
  placeholderText?: string;
  state?: 'default';
  type?: string;
  disabled?: boolean;
  gradient?: Gradient;
  mode?: 'solid' | 'gradient';
  color?: Color | string;
  hasGradient?: boolean;
  palettes?: Palette[];
  canClear?: boolean;
  onChange?: (value: Color | undefined, mode?: 'solid' | 'gradient') => void;
  onChangeComplete?: (value: Color | string, initialValue: Color | string) => void;
  dataTestId?: string;
  'data-qa-selector'?: string;
}

export const ColorField: React.FC<ColorFieldProps> = ({
  className,
  text = 'Color',
  inputValue = '',
  placeholderText = '-',
  disabled = false,
  type = 'text',
  gradient,
  mode = 'solid',
  color,
  onChange,
  onChangeComplete,
  hasGradient = true,
  palettes,
  canClear = true,
  dataTestId,
  'data-qa-selector': dataQaSelector,
}) => {
  const [value, setValue] = useState<string>(inputValue);
  const [showPicker, setShowPicker] = useState(false);
  // Unique instance id used to coordinate exclusive open state across multiple ColorFields
  const instanceId = useId();
  // Generate unique ID for label/input association
  const inputId = useId();
  const colorInputRef = useRef<HTMLDivElement>(null);
  const [positionPicker, setPositionPicker] = useState({
    top: 0,
    left: 0,
  });
  const [placeLeft, setPlaceLeft] = useState(true);
  const [isPositioned, setIsPositioned] = useState(false);
  const [initialColor, setInitialColor] = useState<Color | string>(color);

  const draggingRef = useRef<{
    active: boolean;
    offsetX: number;
    offsetY: number;
  }>({ active: false, offsetX: 0, offsetY: 0 });
  // Once the user drags the picker, we consider its position "manual" and stop auto-repositioning
  const manualPositionedRef = useRef(false);
  useEffect(() => {
    // Ensure external inputValue is also sanitized and capped to 6
    setValue(inputValue);
  }, [inputValue]);

  const onSolidInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === 'gradient') {
      return;
    }
    const newValue = event.target.value;
    setValue(newValue);
  };

  const handleSolidInputChangeComplete = () => {
    if (mode === 'gradient') {
      return;
    }

    const isEmpty = !(value || '').trim();

    if (isEmpty && !canClear) {
      return;
    }

    if (!(value || '').trim()) {
      onChangeComplete?.(undefined, initialColor);
      setInitialColor(undefined);
      return;
    }

    if (!isValidHex(value, true)) {
      // Revert to previous valid value if input is invalid
      setValue(inputValue);
      return;
    }

    const colorObj = new ColorHelper(`#${(value || '').trim().replace('#', '')}`);
    const rgba: RGBA = {
      r: colorObj.red(),
      g: colorObj.green(),
      b: colorObj.blue(),
      a: colorObj.alpha(),
    };
    onChangeComplete?.(rgba, initialColor);
    setInitialColor(rgba);
  };

  const handleClearColor = () => {
    if (mode === 'solid') {
      setValue('');
    }
    onChangeComplete?.(undefined, initialColor);
    setInitialColor(undefined);
  };
  const inputMaxWidth = value.length > 1 ? 'max-w-[112px]' : 'max-w-[88px]';

  const getSolidColorFromPayload = (payload: PickerChangePayload) => {
    let color = ColorHelper('#000000');
    if (payload.mode === 'solid') {
      color = ColorHelper(`#${payload.hex}`).alpha(Math.max(0, Math.min(1, payload.opacity / 100)));
    } else if (payload.mode === 'gradient') {
      // Fallback for gradient: use the first stop as the solid color representation
      const first = payload.gradient?.stops?.[0]?.hexa || '#000000FF';
      color = ColorHelper(first);
    }
    const rgba: RGBA = { r: color.red(), g: color.green(), b: color.blue(), a: color.alpha() };
    return rgba;
  };

  // Map ColorPicker's value to this field's value/handlers
  const handlePickerChange = (payload: PickerChangePayload) => {
    try {
      const color =
        payload.mode === 'gradient' ? payload.gradient : getSolidColorFromPayload(payload);
      onChange?.(color, payload.mode);
    } catch {
      // ignore
    }
  };

  // Map ColorPicker's value to this field's value/handlers
  const handlePickerChangeComplete = (payload: PickerChangePayload) => {
    try {
      let color = null;
      if (payload.reference) {
        color = payload.reference;
      } else {
        color = payload.mode === 'gradient' ? payload.gradient : getSolidColorFromPayload(payload);
      }

      onChangeComplete?.(color, initialColor);
      setInitialColor(color);
    } catch {
      // ignore
    }
  };

  const handleClosePicker = () => {
    setShowPicker(false);
  };

  const handleShowColorPicker = () => {
    // Notify all ColorField instances that this one is opening so others can close
    try {
      window.dispatchEvent(new CustomEvent('lp:colorpicker-open', { detail: { id: instanceId } }));
    } catch {
      /* noop */
    }
    // Reset manual positioning on a fresh open so initial anchoring works
    manualPositionedRef.current = false;
    // Reset positioned state to hide until properly positioned
    setIsPositioned(false);
    const rect = colorInputRef.current?.getBoundingClientRect();
    const gap = 8;
    // Set an initial best-guess position; we'll refine after mount with actual size
    const top = Math.max(8, Math.min(rect?.top ?? 0, window.innerHeight - 380 - 8));
    const leftCandidate = (rect?.left ?? 0) - gap;
    // Try to place left by default; we'll recompute after we know real width
    setPlaceLeft(true);
    const left = leftCandidate;
    setPositionPicker({ top, left });
    setShowPicker(true);
  };
  // Do NOT auto-close on general outside clicks: picker should only close via its own close button.
  // We still capture a ref so callers can position it; we simply won't use the outside-click callback.
  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  // Recompute picker position when it opens, resizes (e.g. Solid -> Gradient), or viewport changes
  useEffect(() => {
    if (!showPicker) return;

    const gap = 8;

    const recompute = () => {
      // If user has manually placed the picker by dragging, don't auto-reposition
      if (manualPositionedRef.current) return;
      const anchorRect = colorInputRef.current?.getBoundingClientRect();
      const pickerEl = colorPickerRef.current;
      if (!anchorRect || !pickerEl) return;

      // Measure current picker size
      const pickerRect = pickerEl.getBoundingClientRect();

      // Decide left/right based on actual width
      const canLeft = anchorRect.left - gap - pickerRect.width >= 8;
      const nextPlaceLeft = canLeft;
      setPlaceLeft(nextPlaceLeft);
      const nextLeft = nextPlaceLeft ? anchorRect.left - gap : anchorRect.right + gap;

      // Clamp vertically within viewport using actual height
      const maxTop = Math.max(8, window.innerHeight - pickerRect.height - 8);
      const nextTop = Math.min(Math.max(8, anchorRect.top), maxTop);

      setPositionPicker({ top: nextTop, left: nextLeft });
      // Mark as positioned to show the picker
      setIsPositioned(true);
    };

    // Recompute on next frame to ensure DOM is painted
    const raf = requestAnimationFrame(recompute);

    // Observe size changes of the picker (Solid -> Gradient etc.)
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined' && colorPickerRef.current) {
      ro = new ResizeObserver(() => recompute());
      ro.observe(colorPickerRef.current);
    }

    // Recompute on viewport changes and scrolling containers
    const onResize = () => recompute();
    const onScroll = () => recompute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      ro?.disconnect();
    };
  }, [showPicker]);

  // Maintain a global counter while any ColorPicker is open so dropdowns ignore outside close
  useEffect(() => {
    const w = window as unknown as { __lpColorPickerOpen?: number };
    if (showPicker) {
      w.__lpColorPickerOpen = (w.__lpColorPickerOpen ?? 0) + 1;
      return () => {
        w.__lpColorPickerOpen = Math.max(0, (w.__lpColorPickerOpen ?? 1) - 1);
      };
    }
    return;
  }, [showPicker]);

  // Enable dragging the floating color picker by its drag handle inside
  useEffect(() => {
    if (!showPicker) return;
    const onMouseDown = (e: MouseEvent) => {
      const pickerEl = colorPickerRef.current;
      if (!pickerEl) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Only start drag if the mousedown happened on the drag handle inside this picker
      if (!pickerEl.contains(target)) return;
      const isHandle = target.closest('.colorpicker-drag-handle');
      if (!isHandle) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = pickerEl.getBoundingClientRect();
      // Lock the current on-screen position first so switching off the left-anchored
      // transform doesn't cause a visual jump.
      setPositionPicker({ left: rect.left, top: rect.top });
      // Move to non-transformed mode to allow free dragging in absolute coordinates
      setPlaceLeft(false);
      manualPositionedRef.current = true;
      draggingRef.current = {
        active: true,
        offsetX: (e.clientX ?? 0) - rect.left,
        offsetY: (e.clientY ?? 0) - rect.top,
      };
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current.active) return;
      const nx = Math.max(
        8,
        Math.min((e.clientX ?? 0) - draggingRef.current.offsetX, window.innerWidth - 8),
      );
      const ny = Math.max(
        8,
        Math.min((e.clientY ?? 0) - draggingRef.current.offsetY, window.innerHeight - 8),
      );
      setPositionPicker({ left: nx, top: ny });
    };
    const onMouseUp = () => {
      if (!draggingRef.current.active) return;
      draggingRef.current.active = false;
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [showPicker]);

  // Listen for other ColorFields opening and close this one if it's not the opener
  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      if (!ev.detail) return;
      // Ignore scroll-triggered close if user has manually detached the picker by dragging
      if (ev.detail.id === '__scroll_close__' && manualPositionedRef.current) return;
      if (ev.detail.id !== instanceId) {
        setShowPicker(false);
      }
    };
    window.addEventListener('lp:colorpicker-open', onOtherOpen as EventListener);
    return () => {
      window.removeEventListener('lp:colorpicker-open', onOtherOpen as EventListener);
    };
  }, [instanceId]);

  return (
    <div
      ref={colorInputRef}
      {...(dataTestId && { 'data-testid': dataTestId })}
      {...(dataQaSelector && { 'data-qa-selector': dataQaSelector })}
      className={cn('flex items-center justify-between', className)}
    >
      {text && (
        <label htmlFor={inputId} className="text-button-sm text-neutral-500">
          {text}
        </label>
      )}

      <div className="relative">
        <>
          {mode === 'solid' ? (
            isValidHex(value, true) ? (
              <div
                onClick={() => (showPicker ? handleClosePicker() : handleShowColorPicker())}
                className="absolute top-[7px] left-1.5 z-10 h-4.5 w-4.5 cursor-pointer rounded border border-neutral-200"
                style={{ backgroundColor: '#' + (value || '').replace('#', '') }}
                data-testid={dataTestId ? `${dataTestId}-swatch` : 'color-swatch'}
                role="button"
                aria-label={`Open color picker for ${text || 'color'}`}
              />
            ) : (
              <div
                onClick={() => (showPicker ? handleClosePicker() : handleShowColorPicker())}
                className="absolute top-[7px] left-1.5 z-10 h-4.5 w-4.5 cursor-pointer overflow-hidden rounded outline outline-offset-[-1px] outline-neutral-200"
                data-testid={dataTestId ? `${dataTestId}-swatch` : 'color-swatch'}
                role="button"
                aria-label={`Open color picker for ${text || 'color'}`}
              >
                <img
                  src={FillPlaceholder}
                  className="h-full w-full object-cover"
                  alt="placeholder"
                />
              </div>
            )
          ) : (
            <div
              onClick={() => (showPicker ? handleClosePicker() : handleShowColorPicker())}
              className="absolute top-[7px] left-1.5 z-10 h-4.5 w-4.5 cursor-pointer rounded border border-neutral-200"
              style={{ background: getGradientString(gradient as Gradient) }}
              data-testid={dataTestId ? `${dataTestId}-swatch` : 'color-swatch'}
              role="button"
              aria-label={`Open color picker for ${text || 'color'}`}
            />
          )}
        </>

        {showPicker &&
          createPortal(
            <div
              ref={colorPickerRef}
              data-colorpicker-portal
              style={{
                position: 'fixed',
                top: positionPicker.top,
                left: positionPicker.left,
                zIndex: 100010,
                maxHeight: 'calc(100vh - 16px)',
                overflow: 'auto',
                transform: placeLeft ? 'translateX(calc(-100% - 8px))' : 'none',
                cursor: draggingRef.current.active ? 'grabbing' : 'default',
                opacity: isPositioned ? 1 : 0,
                visibility: isPositioned ? 'visible' : 'hidden',
              }}
            >
              <ColorPicker
                palettes={palettes}
                title="Color Picker"
                mode={mode}
                hex={inputValue || '000000'}
                opacity={Math.round((getRgbaFromHex(value || '')?.a ?? 1) * 100)}
                onChange={handlePickerChange}
                onChangeComplete={handlePickerChangeComplete}
                onClose={handleClosePicker}
                gradient={gradient}
                hasGradient={hasGradient}
              />
            </div>,
            document.body,
          )}
        <Input
          id={inputId}
          // Mark this as part of the ColorPicker portal so parent dropdowns/inspectors
          // don't treat interactions as "outside" clicks.
          data-colorpicker-portal
          {...(dataTestId && { 'data-testid': `${dataTestId}-input` })}
          onPointerDown={(e) => {
            // Prevent Radix Dropdown/other outside click handlers from closing
            e.stopPropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          className={cn('w-full normal-case transition-all', inputMaxWidth, 'pl-8', {
            'pr-8': mode === 'solid',
          })}
          placeholder={placeholderText}
          type={type}
          value={mode === 'solid' ? sanitizeHexInput(value) : gradient?.type}
          disabled={disabled || mode === 'gradient'}
          onChange={onSolidInputChangeHandler}
          onChangeComplete={handleSolidInputChangeComplete}
        />

        {canClear &&
          ((mode === 'solid' && isValidHex(value, true)) || (mode === 'gradient' && gradient)) && (
            <IconSimpleButton
              icon={IconClose}
              onClick={handleClearColor}
              className="absolute top-1/2 right-1 -translate-y-1/2"
            />
          )}
      </div>
    </div>
  );
};
// Sanitize user input: strip leading '#', keep hex chars only, cap to 6, uppercase
function sanitizeHexInput(raw: string): string {
  if (!raw) return '';
  const noHash = raw.replace(/^#/, '');
  const hexOnly = noHash.replace(/[^0-9a-fA-F]/g, '');
  return hexOnly.slice(0, 6).toUpperCase();
}
